import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CheckCircle2 } from 'lucide-react';
import { C, FONTS } from '../../tokens/design';
import { useBranding } from '../../context/BrandingContext';

const API_BASE = import.meta.env.VITE_API_BASE_URL || '';

export default function ResetPassword() {
  const branding = useBranding();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [done, setDone] = useState(false);
  const [error, setError] = useState(!token ? 'Missing reset token. Check the link in your email.' : '');
  const [loading, setLoading] = useState(false);

  const btnGradient = `linear-gradient(135deg, ${branding?.secondaryColor || '#e94560'}, ${branding?.primaryColor || '#1a1a2e'})`;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirm) { setError('Passwords do not match'); return; }
    if (password.length < 12) { setError('Password must be at least 12 characters'); return; }
    setError('');
    setLoading(true);
    try {
      const resp = await fetch(`${API_BASE}/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, newPassword: password }),
      });
      if (!resp.ok) {
        const data = await resp.json().catch(() => ({}));
        throw new Error(data.error || 'Reset failed');
      }
      setDone(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = {
    width: '100%', background: C.s2, border: `1px solid ${C.b1}`, borderRadius: 8,
    padding: '12px 14px', color: C.t1, fontSize: 13, fontFamily: FONTS.mono, outline: 'none',
    boxSizing: 'border-box',
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: C.bg }}>
      <div style={{ width: 400 }}>
        <div style={{ fontSize: 22, fontWeight: 800, color: C.t1, fontFamily: FONTS.sans, marginBottom: 6 }}>Set New Password</div>
        <div style={{ fontSize: 12, color: C.t2, fontFamily: FONTS.mono, marginBottom: 28 }}>Choose a strong password for your account</div>

        {error && (
          <div style={{ padding: '10px 14px', borderRadius: 8, background: C.redBg, border: `1px solid ${C.red}33`, marginBottom: 16, fontSize: 11, color: C.red, fontFamily: FONTS.mono }}>{error}</div>
        )}

        {done ? (
          <div style={{ padding: 20, background: C.s1, border: `1px solid ${C.b1}`, borderRadius: 10, textAlign: 'center' }}>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 12, color: C.grn }}><CheckCircle2 size={36} strokeWidth={1.5} /></div>
            <div style={{ fontSize: 14, fontWeight: 700, color: C.grn, fontFamily: FONTS.sans, marginBottom: 8 }}>Password Reset Successfully</div>
            <div style={{ fontSize: 11, color: C.t2, fontFamily: FONTS.mono, marginBottom: 20 }}>You can now sign in with your new password.</div>
            <Link to="/login" style={{
              display: 'inline-block', padding: '10px 24px', borderRadius: 8, textDecoration: 'none',
              background: btnGradient, color: '#fff', fontSize: 13, fontWeight: 700, fontFamily: FONTS.sans,
            }}>Sign In</Link>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 10, color: C.t2, fontFamily: FONTS.mono, textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', marginBottom: 6 }}>New Password</label>
              <input value={password} onChange={e => setPassword(e.target.value)} placeholder="••••••••••••" type="password" autoFocus required minLength={12} style={inputStyle} />
            </div>

            <div style={{ marginBottom: 20 }}>
              <label style={{ fontSize: 10, color: C.t2, fontFamily: FONTS.mono, textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', marginBottom: 6 }}>Confirm Password</label>
              <input value={confirm} onChange={e => setConfirm(e.target.value)} placeholder="••••••••••••" type="password" required minLength={12} style={inputStyle} />
            </div>

            <button type="submit" disabled={loading} style={{
              width: '100%', padding: '13px 0', borderRadius: 8, border: 'none', cursor: loading ? 'wait' : 'pointer',
              background: loading ? C.t3 : btnGradient,
              color: '#fff', fontSize: 14, fontWeight: 700, fontFamily: FONTS.sans,
            }}>{loading ? 'Resetting...' : 'Reset Password'}</button>

            <div style={{ display: 'flex', justifyContent: 'center', marginTop: 16 }}>
              <Link to="/login" style={{ fontSize: 11, color: C.sky, fontFamily: FONTS.mono, textDecoration: 'none' }}>{'\u2190'} Back to sign in</Link>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
